import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { supabase } from 'lib/supabase';
import Icon from 'components/AppIcon';
import logoImage from '/assets/images/logo-volaptic-1775131861970.png';

const PRO_AVATAR_ICONS = [
  { id: 'person', label: 'Persona', svg: (color) => (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
      <circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
    </svg>
  )},
  { id: 'wrench', label: 'Chiave inglese', svg: (color) => (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
      <path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"/>
    </svg>
  )},
  { id: 'hammer', label: 'Martello', svg: (color) => (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
      <path d="M15 12l-8.5 8.5c-.83.83-2.17.83-3 0 0 0 0 0 0 0a2.12 2.12 0 010-3L12 9"/>
      <path d="M17.64 15L22 10.64"/><path d="M20.91 11.7l-1.25-1.25c-.6-.6-.93-1.4-.93-2.25v-.86L16.01 4.6a5.56 5.56 0 00-3.94-1.64H9l.92.82A6.18 6.18 0 0112 8.4v1.56l2 2h2.47l2.26 1.91"/>
    </svg>
  )},
  { id: 'zap', label: 'Fulmine', svg: (color) => (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
    </svg>
  )},
  { id: 'droplets', label: 'Idraulica', svg: (color) => (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
      <path d="M7 16.3c2.2 0 4-1.83 4-4.05 0-1.16-.57-2.26-1.71-3.19S7.29 6.75 7 5.3c-.29 1.45-1.14 2.84-2.29 3.76S3 11.1 3 12.25c0 2.22 1.8 4.05 4 4.05z"/>
      <path d="M12.56 6.6A10.97 10.97 0 0014 3.02c.5 2.5 2 4.9 4 6.5s3 3.5 3 5.5a6.98 6.98 0 01-11.91 4.97"/>
    </svg>
  )},
  { id: 'leaf', label: 'Verde', svg: (color) => (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
      <path d="M17 8C8 10 5.9 16.17 3.82 19.34a1 1 0 001.38 1.4C7.2 19.1 9.5 17 12 17c4 0 8-3 8-8 0-3-1-5-3-7z"/>
      <path d="M3 21c0-4 2-7 5-9"/>
    </svg>
  )},
  { id: 'star', label: 'Stella', svg: (color) => (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
    </svg>
  )},
  { id: 'home', label: 'Casa', svg: (color) => (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
      <path d="M3 9.5L12 3l9 6.5V20a1 1 0 01-1 1H4a1 1 0 01-1-1V9.5z"/><path d="M9 21V12h6v9"/>
    </svg>
  )},
];

const ProfessionalDashboardNavbar = ({ billingExempt = false, vipBadge = { label: 'Volaptic VIP', icon: '🌟' } }) => {
  const navigate = useNavigate();
  const { user, signOut, isAdmin } = useAuth();
  const [userName, setUserName] = useState('');
  const [userRole, setUserRole] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [avatarIcon, setAvatarIcon] = useState(null);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!user) return;
      try {
        const { data, error } = await supabase
          ?.from('users')
          ?.select('email, role, full_name, avatar_url, avatar_icon')
          ?.eq('id', user?.id)
          ?.single();

        if (!error && data) {
          if (data?.email) {
            const namePart = data?.email?.split('@')?.[0];
            const fallbackName = namePart?.charAt(0)?.toUpperCase() + namePart?.slice(1);
            setUserName(data?.full_name || fallbackName);
          }
          if (data?.role) {
            setUserRole(data?.role);
          }
          if (data?.avatar_url) {
            setAvatarUrl(data?.avatar_url);
          }
          if (data?.avatar_icon) setAvatarIcon(data?.avatar_icon);
        } else if (user?.email) {
          const namePart = user?.email?.split('@')?.[0];
          setUserName(namePart?.charAt(0)?.toUpperCase() + namePart?.slice(1));
        }
      } catch (err) {
        if (user?.email) {
          const namePart = user?.email?.split('@')?.[0];
          setUserName(namePart?.charAt(0)?.toUpperCase() + namePart?.slice(1));
        }
      }
    };
    fetchUserData();
  }, [user]);

  useEffect(() => {
    const handleAvatarUpdate = (e) => {
      const { icon, url } = e?.detail || {};
      setAvatarUrl(url ?? null);
      setAvatarIcon(icon ?? null);
    };
    const handleNameUpdate = (e) => {
      const { name } = e?.detail || {};
      if (name !== undefined) setUserName(name);
    };
    window.addEventListener('volaptic-avatar-update', handleAvatarUpdate);
    window.addEventListener('volaptic-name-update', handleNameUpdate);
    return () => {
      window.removeEventListener('volaptic-avatar-update', handleAvatarUpdate);
      window.removeEventListener('volaptic-name-update', handleNameUpdate);
    };
  }, []);

  const handleLogout = async () => {
    setMobileMenuOpen(false);
    const { error } = await signOut();
    if (!error) {
      navigate('/login-form');
    }
  };

  const isAdminUser = userRole === 'superadmin' || userRole === 'admin';

  // Get first 2 letters of username for avatar initials
  const initials = userName ? userName?.slice(0, 2)?.toUpperCase() : '??';

  return (
    <header
      className="header-nav"
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 24px', width: '100%' }}
    >
      {/* Logo + VIP pill (left side) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <Link to="/" className="header-logo flex items-center">
          <img
            src={logoImage}
            alt="Volaptic"
            style={{ height: '56px', width: 'auto' }}
          />
        </Link>
        {/* VIP pill — desktop only, next to logo */}
        {billingExempt && (
          <div className="hidden md:inline-flex relative group flex-shrink-0">
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '3px 10px',
                borderRadius: '999px',
                background: 'linear-gradient(135deg, #013776 0%, #32b3e5 100%)',
                color: '#fff',
                fontSize: '12px',
                fontWeight: 700,
                letterSpacing: '0.01em',
                whiteSpace: 'nowrap',
                cursor: 'default',
                boxShadow: '0 1px 6px rgba(1,55,118,0.25)',
              }}
            >
              <span style={{ fontSize: '13px' }}>{vipBadge?.icon}</span>
              <span className="hidden sm:inline">{vipBadge?.label}</span>
            </span>
            {/* Tooltip */}
            <span
              style={{
                position: 'absolute',
                top: '110%',
                left: 0,
                background: '#013776',
                color: '#fff',
                fontSize: '11px',
                fontWeight: 500,
                padding: '4px 9px',
                borderRadius: '5px',
                whiteSpace: 'nowrap',
                pointerEvents: 'none',
                opacity: 0,
                transition: 'opacity 0.15s',
                zIndex: 20,
                marginTop: '4px',
              }}
              className="group-hover:opacity-100"
            >
              Preventivi illimitati gratis
            </span>
          </div>
        )}
      </div>
      {/* Desktop: user identity + settings + logout */}
      <div className="hidden md:flex items-center gap-3">
        {/* Admin route selector dropdown */}
        {isAdmin && (
          <select
            value={typeof window !== 'undefined' ? window.location?.pathname : '/'}
            onChange={(e) => navigate(e?.target?.value)}
            style={{
              padding: '4px 8px',
              fontSize: '12px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              background: '#f9fafb',
              cursor: 'pointer',
              maxWidth: '200px',
            }}
          >
            <option value="/">🏠 Home</option>
            <optgroup label="Professionista">
              <option value="/professional-dashboard">📊 Dashboard</option>
              <option value="/professional/settings">⚙️ Impostazioni</option>
              <option value="/professional/trattative">Trattative</option>
            </optgroup>
            <optgroup label="Cliente">
              <option value="/cliente/dashboard">👤 Dashboard</option>
              <option value="/cliente/settings">⚙️ Impostazioni</option>
              <option value="/cliente/richiesta/nuova">Nuova Richiesta</option>
            </optgroup>
            <optgroup label="Admin">
              <option value="/admin">🛡️ Overview</option>
              <option value="/admin/clienti">Clienti</option>
              <option value="/admin/professionisti">Professionisti</option>
              <option value="/admin/richieste">Richieste</option>
              <option value="/admin/preventivi">Preventivi</option>
              <option value="/admin/audit-log">Audit</option>
              <option value="/admin/settings">Impostazioni</option>
            </optgroup>
            <optgroup label="Auth">
              <option value="/login-form">Login</option>
              <option value="/signup-form">Signup</option>
              <option value="/payment-verification">Payment</option>
            </optgroup>
          </select>
        )}
        {/* Dashboard link — always goes to /professional-dashboard for all roles */}
        <Link
          to="/professional-dashboard"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '5px',
            padding: '6px 12px',
            borderRadius: '8px',
            color: '#013776',
            fontSize: '13px',
            fontWeight: 600,
            textDecoration: 'none',
            transition: 'background 0.2s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = '#f0f9ff'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
        >
          <Icon name="LayoutDashboard" size={15} color="#013776" />
          Dashboard
        </Link>
        {/* Admin link — only for privileged roles */}
        {isAdminUser && (
          <Link
            to="/admin"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '5px',
              padding: '6px 12px',
              borderRadius: '8px',
              color: '#013776',
              fontSize: '13px',
              fontWeight: 600,
              textDecoration: 'none',
              transition: 'background 0.2s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = '#f0f9ff'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            <Icon name="Shield" size={15} color="#013776" />
            Admin
          </Link>
        )}

        {/* Avatar + Name/Role */}
        <div className="flex items-center gap-2.5">
          {/* Gradient Avatar or Photo or Icon */}
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt="Avatar"
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                objectFit: 'cover',
                flexShrink: 0,
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          ) : avatarIcon ? (
            <div style={{
              width: '36px', height: '36px', borderRadius: '50%',
              background: 'linear-gradient(135deg, #e8f0fb 0%, #c3d9f5 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, boxShadow: '0 2px 6px rgba(0,0,0,0.12)'
            }}>
              {PRO_AVATAR_ICONS?.find(i => i?.id === avatarIcon)?.svg('#2780b5')}
            </div>
          ) : (
            <div
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #32b3e5 0%, #013776 100%)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <span style={{ color: '#fff', fontWeight: 700, fontSize: '13px', letterSpacing: '0.5px' }}>
                {initials}
              </span>
            </div>
          )}
          {/* Name + Role */}
          <div className="flex flex-col leading-tight">
            {userName && (
              <span style={{ color: '#013776', fontWeight: 600, fontSize: '14px', lineHeight: '1.2' }}>
                {userName}
              </span>
            )}
            <span style={{ color: '#32b3e5', fontSize: '11px', fontWeight: 500, lineHeight: '1.3', marginTop: '1px' }}>
              Professionista
            </span>
          </div>
        </div>

        {/* Settings icon-only button */}
        <div className="relative group">
          <Link
            to="/professional/settings"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '7px',
              borderRadius: '8px',
              color: '#32b3e5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background 0.2s',
              textDecoration: 'none',
            }}
            onMouseEnter={e => e.currentTarget.style.background = '#f0f9ff'}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}
            aria-label="Impostazioni"
          >
            <Icon name="Settings" size={18} color="#32b3e5" />
          </Link>
          {/* Tooltip */}
          <span
            style={{
              position: 'absolute',
              top: '110%',
              left: '50%',
              transform: 'translateX(-50%)',
              background: '#013776',
              color: '#fff',
              fontSize: '11px',
              fontWeight: 500,
              padding: '3px 8px',
              borderRadius: '5px',
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
              opacity: 0,
              transition: 'opacity 0.15s',
              zIndex: 10,
            }}
            className="group-hover:opacity-100"
          >
            Impostazioni
          </span>
        </div>

        {/* Thin vertical separator */}
        <div
          style={{
            width: '1px',
            height: '28px',
            background: '#e2e8f0',
            flexShrink: 0,
          }}
        />

        {/* Esci pill button */}
        <button
          onClick={handleLogout}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 16px',
            borderRadius: '999px',
            border: '1.5px solid #32b3e5',
            background: 'transparent',
            color: '#013776',
            fontSize: '13px',
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'background 0.2s, color 0.2s',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background = '#32b3e5';
            e.currentTarget.style.color = '#fff';
            const icon = e?.currentTarget?.querySelector('svg');
            if (icon) icon.style.stroke = '#fff';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = '#013776';
            const icon = e?.currentTarget?.querySelector('svg');
            if (icon) icon.style.stroke = '#013776';
          }}
        >
          <Icon name="LogOut" size={14} color="#013776" />
          Esci
        </button>
      </div>
      {/* Mobile: hamburger icon */}
      <div className="flex md:hidden items-center">
        <button
          onClick={() => setMobileMenuOpen((prev) => !prev)}
          className="p-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
          aria-label="Menu"
        >
          <Icon name={mobileMenuOpen ? 'X' : 'Menu'} size={24} />
        </button>
      </div>
      {/* Mobile dropdown menu */}
      {mobileMenuOpen && (
        <div
          className="absolute top-16 left-0 right-0 z-50 bg-white border-b border-gray-200 shadow-lg px-6 py-4 flex flex-col gap-3 md:hidden"
        >
          {/* User identity */}
          <div className="flex items-center gap-3 pb-3 border-b border-gray-100">
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt="Avatar"
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  flexShrink: 0,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            ) : avatarIcon ? (
              <div style={{
                width: '36px', height: '36px', borderRadius: '50%',
                background: 'linear-gradient(135deg, #e8f0fb 0%, #c3d9f5 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, boxShadow: '0 2px 6px rgba(0,0,0,0.12)'
              }}>
                {PRO_AVATAR_ICONS?.find(i => i?.id === avatarIcon)?.svg('#2780b5')}
              </div>
            ) : (
              <div
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #32b3e5 0%, #013776 100%)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <span style={{ color: '#fff', fontWeight: 700, fontSize: '13px' }}>{initials}</span>
              </div>
            )}
            <div>
              {userName && (
                <p style={{ color: '#013776', fontWeight: 600, fontSize: '14px' }}>{userName}</p>
              )}
              <p style={{ color: '#32b3e5', fontSize: '11px', fontWeight: 500 }}>Professionista</p>
            </div>
          </div>
          {/* Admin route selector in mobile menu */}
          {isAdmin && (
            <div className="pb-2 border-b border-gray-100">
              <select
                value={typeof window !== 'undefined' ? window.location?.pathname : '/'}
                onChange={(e) => { navigate(e?.target?.value); setMobileMenuOpen(false); }}
                style={{
                  padding: '4px 8px',
                  fontSize: '12px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  background: '#f9fafb',
                  cursor: 'pointer',
                  maxWidth: '200px',
                  width: '100%',
                }}
              >
                <option value="/">🏠 Home</option>
                <optgroup label="Professionista">
                  <option value="/professional-dashboard">📊 Dashboard</option>
                  <option value="/professional/settings">⚙️ Impostazioni</option>
                  <option value="/professional/trattative">Trattative</option>
                </optgroup>
                <optgroup label="Cliente">
                  <option value="/cliente/dashboard">👤 Dashboard</option>
                  <option value="/cliente/settings">⚙️ Impostazioni</option>
                  <option value="/cliente/richiesta/nuova">Nuova Richiesta</option>
                </optgroup>
                <optgroup label="Admin">
                  <option value="/admin">🛡️ Overview</option>
                  <option value="/admin/clienti">Clienti</option>
                  <option value="/admin/professionisti">Professionisti</option>
                  <option value="/admin/richieste">Richieste</option>
                  <option value="/admin/preventivi">Preventivi</option>
                  <option value="/admin/audit-log">Audit</option>
                  <option value="/admin/settings">Impostazioni</option>
                </optgroup>
                <optgroup label="Auth">
                  <option value="/login-form">Login</option>
                  <option value="/signup-form">Signup</option>
                  <option value="/payment-verification">Payment</option>
                </optgroup>
              </select>
            </div>
          )}
          {/* Admin links in mobile menu */}
          {/* Dashboard link in mobile — always goes to /professional-dashboard */}
          <Link
            to="/professional-dashboard"
            onClick={() => setMobileMenuOpen(false)}
            className="flex items-center gap-2 text-sm font-medium py-2 text-gray-700 hover:text-blue-600 transition-colors"
          >
            <Icon name="LayoutDashboard" size={16} />
            Dashboard
          </Link>
          {isAdminUser && (
            <Link
              to="/admin"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-2 text-sm font-medium py-2 text-gray-700 hover:text-blue-600 transition-colors"
            >
              <Icon name="Shield" size={16} />
              Admin
            </Link>
          )}
          {/* Settings in mobile menu */}
          <Link
            to="/professional/settings"
            onClick={() => setMobileMenuOpen(false)}
            className="flex items-center gap-2 text-sm font-medium py-2 text-gray-700 hover:text-blue-600 transition-colors"
          >
            <Icon name="Settings" size={16} />
            Impostazioni
          </Link>
          {/* Logout */}
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-sm font-medium py-2 text-red-600 hover:text-red-700 transition-colors"
          >
            <Icon name="LogOut" size={16} />
            Esci
          </button>
        </div>
      )}
    </header>
  );
};

export default ProfessionalDashboardNavbar;
